import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Alert, Button, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import LeagueCleanupDialog from '../components/admin/LeagueCleanupDialog';
import { showNotification } from '../store/slices/notificationSlice';
import { clearAllOldData } from '../services/adminService';
import type { AppDispatch, RootState } from '../store/store';

const CleanupPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clearingOldData, setClearingOldData] = useState(false);
  const isAdmin = user?.roles?.includes('admin');

  const handleClearAllOldData = async () => {
    if (
      !window.confirm(
        'Are you sure you want to clear all old data? This will delete all item snapshots older than 14 days and characters not alive for more than 30 days. This action cannot be undone.',
      )
    ) {
      return;
    }

    setClearingOldData(true);

    try {
      const result = await clearAllOldData();
      dispatch(
        showNotification({
          message: `Successfully cleared old data: ${result.itemSnapshotsDeleted} item snapshots and ${result.charactersDeleted} characters deleted`,
          type: 'success',
        }),
      );
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to clear old data';

      dispatch(
        showNotification({
          message: `Failed to clear old data: ${message}`,
          type: 'error',
        }),
      );
    } finally {
      setClearingOldData(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to='/' replace />;
  }

  return (
    <PageLayout>
      <PageHeader icon={<DeleteSweepIcon />} title='Cleanup' subtitle='Admin-only destructive cleanup operations with progress tracking' />

      <ContentSection>
        <Stack spacing={3}>
          <EnhancedCard variant='outlined'>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <div>
                  <Typography variant='h6' sx={{ mb: 1 }}>
                    League cleanup
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Select a game and the single league to keep. The cleanup job will remove every other league in that game in chunks and report
                    progress while it runs.
                  </Typography>
                </div>

                <Alert severity='warning'>This action is permanent and should only be run by administrators after confirming the target game and keep league.</Alert>

                <BoxAction>
                  <Button variant='contained' color='error' startIcon={<DeleteSweepIcon />} onClick={() => setDialogOpen(true)}>
                    Open League Cleanup
                  </Button>
                </BoxAction>
              </Stack>
            </CardContent>
          </EnhancedCard>

          <EnhancedCard variant='outlined'>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <div>
                  <Typography variant='h6' sx={{ mb: 1 }}>
                    Old data cleanup
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Remove item snapshots older than 14 days and characters that have not been alive for more than 30 days.
                  </Typography>
                </div>

                <Alert severity='warning'>This cleanup is also permanent. Use it only when you intend to purge stale historical data.</Alert>

                <BoxAction>
                  <Button
                    variant='contained'
                    color='error'
                    onClick={handleClearAllOldData}
                    disabled={clearingOldData}
                    startIcon={clearingOldData ? <CircularProgress size={18} color='inherit' /> : <DeleteSweepIcon />}
                  >
                    {clearingOldData ? 'Clearing old data...' : 'Clear All Old Data'}
                  </Button>
                </BoxAction>
              </Stack>
            </CardContent>
          </EnhancedCard>
        </Stack>
      </ContentSection>

      <LeagueCleanupDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </PageLayout>
  );
};

const BoxAction: React.FC<{ children: React.ReactNode }> = ({ children }) => <div>{children}</div>;

export default CleanupPage;